import { Flex, Text, VStack } from '@chakra-ui/react';
import {
  Button,
  Divider,
  DividerLook,
  FieldHelpText,
  Icons,
  InputVariant,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { ActivityChangeFn } from 'components/Form/ActivityTree/utils';
import { Activity } from 'generated/models';
import { I18nProps, withTranslations } from 'i18n';
import { useCallback } from 'react';
import { SetValueConfig, useFieldArray, useFormContext } from 'react-hook-form';
import ActivityTree from '../ActivityTree';

interface ActivityTreeGroupControl extends I18nProps {
  activities: Activity[];
  isChangeAllowed: (index: number, terActivity: string) => Promise<boolean>;
  isDisabled?: boolean;
  isFirstChangeable?: boolean;
  name: 'activities';
  readonly?: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
}

export interface ActivitySelection {
  activity: string;
  subActivity: string;
  terActivity: string;
}

const template: ActivitySelection = {
  activity: '',
  subActivity: '',
  terActivity: '',
};

interface ActivityFieldValues {
  activities: ActivitySelection[];
}

// TODO: rewrite
const ActivityTreeGroupControl = ({
  activities,
  isChangeAllowed,
  isDisabled,
  isFirstChangeable,
  name,
  readonly,
  onAdd,
  onRemove,
  t,
}: ActivityTreeGroupControl) => {
  const { append, fields, remove, update } = useFieldArray<ActivityFieldValues>({ name });
  const { formState, setValue } = useFormContext<ActivityFieldValues>();
  const { errors } = formState;

  const hasElements = fields?.length > 0;

  const handleActivityTreeChange = useCallback<ActivityChangeFn>(
    async (
      index,
      activity,
      subActivity,
      terActivity,
      activityFieldName,
      subActivityFieldName,
      terActivityFieldName,
    ) => {
      if (await isChangeAllowed(index, terActivity)) {
        const options: SetValueConfig = {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        };
        update(index, { activity, subActivity, terActivity });
        // @ts-expect-error Hard to type it
        setValue(activityFieldName, activity, options);
        // @ts-expect-error Hard to type it
        setValue(subActivityFieldName, subActivity, options);
        // @ts-expect-error Hard to type it
        setValue(terActivityFieldName, terActivity, options);
      }
    },
    [isChangeAllowed, setValue, update],
  );

  const addButton = !isDisabled && (
    <Button
      type="button"
      onClick={() => {
        onAdd?.();
        append(template);
      }}
      variant="secondaryOutlined"
      id="Form_ActivityTree_addButton"
      size="md"
      alignSelf="flex-start"
    >
      {t('location_add.buttons.addActivity', 'Add new')}
    </Button>
  );

  return (
    <>
      {fields.map((field, index) => {
        const deleteButton = (
          <Button
            type="button"
            onClick={async () => {
              onRemove?.();
              if (await isChangeAllowed(index, '')) {
                remove(index);
              }
            }}
            variant="secondaryTextOnly"
            id={`Form_ActivityTree_${index}_deleteButton`}
            size="md"
            alignSelf="flex-end"
            leftIcon={<SvgSpriteConsumer size={24} svgId={Icons.TRASH24} />}
          >
            {t('location_add.buttons.removeActivity', 'Remove')}
          </Button>
        );

        return (
          <VStack align="stretch" spacing={4} key={`activity-field-${field.id}`}>
            <Text as="div" variant="subtitle.section">
              {index + 1}. {t('location_add.sectionTexts.addLocation.activity', 'Activity')}
            </Text>
            <ActivityTree
              readonly={readonly}
              name={`${name}.${index}`}
              activities={activities}
              index={index}
              isDisabled={isDisabled}
              isFirstChangeable={isFirstChangeable}
              onChange={handleActivityTreeChange}
            />
            {!readonly && (
              <>
                {hasElements && index === fields.length - 1 ? (
                  <Flex justify="space-between">
                    {addButton}
                    {deleteButton}
                  </Flex>
                ) : (
                  deleteButton
                )}
              </>
            )}
            {index + 1 < fields.length && <Divider look={DividerLook.SECONDARY} variant="solid" />}
          </VStack>
        );
      })}
      {errors[name]?.message && (
        <FieldHelpText
          disabled={isDisabled}
          text={errors[name].message}
          variant={InputVariant.ERROR}
        />
      )}
      {!readonly && !hasElements && addButton}
    </>
  );
};

export default withTranslations(ActivityTreeGroupControl);
